import type { AuthenticationResultType } from "@aws-sdk/client-cognito-identity-provider";
import {
	CognitoIdentityProviderClient,
	ConfirmSignUpCommand,
	GetUserAttributeVerificationCodeCommand,
	GetUserCommand,
	InitiateAuthCommand,
	NotAuthorizedException,
	ResendConfirmationCodeCommand,
	RespondToAuthChallengeCommand,
	SignUpCommand,
	UpdateUserAttributesCommand,
	UserNotFoundException,
	VerifyUserAttributeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { CognitoJwtVerifier } from "aws-jwt-verify";
import { JwtExpiredError } from "aws-jwt-verify/error";
import { SimpleJwksCache } from "aws-jwt-verify/jwk";
import type {
	AuthUser,
	AccessToken,
	RefreshToken,
	IdToken,
} from "./auth-user.ts";
import type { AuthUserAttributes } from "./mapping.ts";
import {
	cognitoAttributesToProfile,
	profileToCognitoAttributes,
} from "./mapping.ts";
import { tokensToAuthUser } from "./tokens.ts";
import validateCompleteAuthResult from "./validate-complete-auth-result.ts";

function isErrorNamed(error: unknown, name: string): error is Error {
	return error instanceof Error && error.name === name;
}

type FullSignUpUser = Pick<
	AuthUser,
	| "firstName"
	| "lastName"
	| "emailAddress"
	| "birthdate"
	| "country"
	| "howHeard"
> &
	Partial<Pick<AuthUser, "ipAddress" | "ipAddressCountry">>;

type MinimalSignUpUser = Pick<AuthUser, "firstName" | "emailAddress"> &
	Partial<
		Pick<AuthUser, "middleName" | "lastName" | "ipAddress" | "ipAddressCountry">
	>;

type Config = {
	readonly userPoolId: string;
	readonly appClientId: string;
	readonly originSite: string;
};

type SignInResult =
	| {
			readonly type: "success";
			readonly session: string;
	  }
	| {
			readonly type: "not-found";
	  };

type SignUpResult = {
	readonly session: string;
	readonly userId: string;
};

type ConfirmSignUpRequest = {
	readonly session: string | undefined;
	readonly emailAddress: string;
	readonly code: string;
};

type SignInConfirmRequest = {
	readonly session: string;
	readonly emailAddress: string;
	readonly code: string;
};

function createAuthService({ userPoolId, appClientId, originSite }: Config) {
	const [region] = userPoolId.split("_");
	const client = new CognitoIdentityProviderClient({
		region,
	});

	// In-memory, not web storage: this runs during AuthProvider's first render,
	// and Safari with cookies blocked has no localStorage binding at all, so
	// touching it here takes the whole app down. Cognito sends cache-control
	// headers on the JWKS, so the browser's HTTP cache still spans page loads.
	const jwksCache = new SimpleJwksCache();
	// graceSeconds is intentionally NEGATIVE. aws-jwt-verify treats a token as
	// expired when `exp + graceSeconds < now` (and not-yet-valid when
	// `nbf - graceSeconds > now`), so -60 makes us consider a token expired 60s
	// BEFORE its real `exp`. That margin is deliberate: it covers a user's clock
	// being a bit off and the lag between validating a token here and actually
	// using it against the API, so we refresh proactively instead of handing a
	// token to a request that's about to (or just did) expire server-side.
	// Side effect: with a token that carries `nbf`, validity also starts 60s
	// later than issuance - harmless for Cognito access/id tokens, which don't
	// rely on nbf.
	const accessVerifier = CognitoJwtVerifier.create(
		{
			userPoolId,
			tokenUse: "access",
			clientId: appClientId,
			graceSeconds: -60,
		},
		{
			jwksCache,
		},
	);
	const idVerifier = CognitoJwtVerifier.create(
		{
			userPoolId,
			tokenUse: "id",
			clientId: appClientId,
			graceSeconds: -60,
		},
		{
			jwksCache,
		},
	);

	async function refreshAuthUserUncached({
		refreshToken,
	}: RefreshToken): Promise<AuthUser | undefined> {
		// To use the refresh token to get new ID and access tokens with the user pools API, use the
		// AdminInitiateAuth or InitiateAuth API operations.
		// Pass REFRESH_TOKEN_AUTH for the AuthFlow parameter.
		// In the AuthParameters property of AuthFlow, pass your user's refresh token as the value of "REFRESH_TOKEN"
		let result: AuthenticationResultType | undefined;
		try {
			const refreshResult = await client.send(
				new InitiateAuthCommand({
					ClientId: appClientId,
					AuthFlow: "REFRESH_TOKEN_AUTH",
					AuthParameters: {
						REFRESH_TOKEN: refreshToken,
					},
				}),
			);
			result = refreshResult.AuthenticationResult;
		} catch (e) {
			if (
				typeof e === "object" &&
				!!e &&
				"message" in e &&
				typeof e.message === "string" &&
				e.message.includes("Refresh Token has expired")
			) {
				return undefined;
			}

			if (
				isErrorNamed(e, "NotAuthorizedException") &&
				e.message.toLowerCase().includes("Invalid Refresh Token".toLowerCase())
			) {
				return undefined;
			}

			throw e;
		}

		// Refresh token invalid?
		const validAuthResult = validateCompleteAuthResult(result, refreshToken);
		if (validAuthResult === undefined) {
			// eslint-disable-next-line no-console
			console.log(
				"Refresh token invalid, force sign out",
				refreshToken,
				result,
			);
			return undefined;
		}

		return tokensToAuthUser({
			accessToken: { accessToken: validAuthResult.AccessToken },
			idToken: { idToken: validAuthResult.IdToken },
			refreshToken: { refreshToken: validAuthResult.RefreshToken },
		});
	}

	// Dedupe concurrent refreshes by refresh-token value. The GraphQL client runs
	// validateTokensAndRefreshIfNeeded in requestMiddleware on EVERY request, so on
	// page load (many parallel queries) an expired access token would otherwise fire
	// N simultaneous InitiateAuth refreshes with the same refresh token - redundant,
	// and a logout risk if refresh-token rotation is ever enabled (later requests
	// would present an already-consumed token). Sharing one in-flight promise per
	// token collapses the stampede; the entry is cleared once it settles.
	const inFlightRefreshes = new Map<string, Promise<AuthUser | undefined>>();
	async function refreshAuthUser(
		token: RefreshToken,
	): Promise<AuthUser | undefined> {
		const existing = inFlightRefreshes.get(token.refreshToken);
		if (existing !== undefined) {
			return existing;
		}
		const promise = refreshAuthUserUncached(token).finally(() => {
			inFlightRefreshes.delete(token.refreshToken);
		});
		inFlightRefreshes.set(token.refreshToken, promise);
		return promise;
	}

	function expectedAuthenticationResultToUser(
		authenticationResult: AuthenticationResultType | undefined,
	) {
		if (authenticationResult === undefined) {
			throw new Error("Error trying to login");
		}

		const validAuthResult = validateCompleteAuthResult(authenticationResult);
		if (validAuthResult === undefined) {
			throw new Error("Error trying to login - no tokens");
		}

		const user = tokensToAuthUser({
			refreshToken: { refreshToken: validAuthResult.RefreshToken },
			idToken: { idToken: validAuthResult.IdToken },
			accessToken: { accessToken: validAuthResult.AccessToken },
		});
		if (user === undefined) {
			throw new Error("Invalid tokens for new sign in confirm");
		}
		return user;
	}

	async function validateTokens(
		accessToken: AccessToken,
		idToken: IdToken,
		refreshToken: RefreshToken,
	): Promise<AuthUser | undefined> {
		try {
			await Promise.all([
				accessVerifier.verify(accessToken.accessToken),
				idVerifier.verify(idToken.idToken),
			]);

			const user = tokensToAuthUser({
				accessToken,
				idToken,
				refreshToken,
			});
			if (user === undefined) {
				throw new Error(
					"Expected to be able to create user from new token payloads",
				);
			}
			return user;
		} catch (e) {
			if (e instanceof JwtExpiredError) {
				// eslint-disable-next-line no-console
				console.log("Tokens expired", e);
				return undefined;
			}

			console.error("Error validating current tokens", e);
			return undefined;
		}
	}

	return {
		validateTokens,
		async validateTokensAndRefreshIfNeeded(
			accessToken: AccessToken,
			idToken: IdToken,
			refreshToken: RefreshToken,
		) {
			const validated = await validateTokens(
				accessToken,
				idToken,
				refreshToken,
			);
			if (validated !== undefined) {
				return validated;
			}

			return refreshAuthUser(refreshToken);
		},
		async minimalSignUp({
			emailAddress,
			...profile
		}: MinimalSignUpUser): Promise<SignUpResult> {
			const response = await client.send(
				new SignUpCommand({
					ClientId: appClientId,
					Username: emailAddress,
					UserAttributes: profileToCognitoAttributes({
						originSite,
						currentSite: originSite,
						...profile,
					}),
					ValidationData: [{ Name: "signUpType", Value: "minimal" }],
				}),
			);
			const { Session, UserSub } = response;
			if (Session === undefined || UserSub === undefined) {
				throw new Error("There was a problem signing the user up");
			}
			return { session: Session, userId: UserSub };
		},
		async fullSignUp({
			emailAddress,
			...profile
		}: MinimalSignUpUser): Promise<SignUpResult> {
			const response = await client.send(
				new SignUpCommand({
					ClientId: appClientId,
					Username: emailAddress,
					UserAttributes: profileToCognitoAttributes({
						originSite,
						currentSite: originSite,
						...profile,
					}),
					ValidationData: [{ Name: "signUpType", Value: "full" }],
				}),
			);
			const { Session, UserSub } = response;
			if (Session === undefined || UserSub === undefined) {
				throw new Error("There was a problem signing the user up");
			}
			return { session: Session, userId: UserSub };
		},
		async resendSignUpConfirmationCode(emailAddress: string) {
			await client.send(
				new ResendConfirmationCodeCommand({
					ClientId: appClientId,
					SecretHash: undefined,
					Username: emailAddress,
				}),
			);
		},
		async confirmSignUpAndSignIn({
			emailAddress,
			session,
			code,
		}: ConfirmSignUpRequest) {
			try {
				const { Session: confirmedSession } = await client.send(
					new ConfirmSignUpCommand({
						ClientId: appClientId,
						Username: emailAddress,
						ConfirmationCode: code,
						Session: session,
					}),
				);
				if (confirmedSession === undefined) {
					throw new Error("There was an error confirming sign up");
				}
				const { AuthenticationResult } = await client.send(
					new InitiateAuthCommand({
						ClientId: appClientId,
						AuthFlow: "USER_AUTH",
						Session: confirmedSession,
						AuthParameters: {
							USERNAME: emailAddress,
						},
					}),
				);
				return {
					type: "success",
					user: expectedAuthenticationResultToUser(AuthenticationResult),
				} as const;
			} catch (e) {
				if (e instanceof NotAuthorizedException) {
					if (
						e.message ===
						"User cannot be confirmed. Current status is CONFIRMED"
					) {
						return { type: "already-confirmed" } as const;
					}
				}

				throw e;
			}
		},
		// Use this for creator app where haven't confirmed email
		async signInNewMinimalSignUp({ userId, session }: SignUpResult) {
			const { AuthenticationResult } = await client.send(
				new InitiateAuthCommand({
					AuthFlow: "CUSTOM_AUTH",
					ClientId: appClientId,
					AuthParameters: {
						USERNAME: userId,
					},
					Session: session,
				}),
			);
			return expectedAuthenticationResultToUser(AuthenticationResult);
		},
		// Use this for email verification auto-login
		async signInWithEmailVerification(userId: string, nonce: string) {
			// Step 1: Initiate CUSTOM_AUTH flow (will receive CUSTOM_CHALLENGE)
			const initiateResponse = await client.send(
				new InitiateAuthCommand({
					AuthFlow: "CUSTOM_AUTH",
					ClientId: appClientId,
					AuthParameters: {
						USERNAME: userId,
					},
				}),
			);

			// Email verification flow should always return CUSTOM_CHALLENGE, never tokens immediately
			if (
				initiateResponse.ChallengeName !== "CUSTOM_CHALLENGE" ||
				!initiateResponse.Session
			) {
				throw new Error(
					`Expected CUSTOM_CHALLENGE but got: ${initiateResponse.ChallengeName}`,
				);
			}

			// Step 2: Respond to the CUSTOM_CHALLENGE with the nonce as the ANSWER
			const { AuthenticationResult } = await client.send(
				new RespondToAuthChallengeCommand({
					ChallengeName: "CUSTOM_CHALLENGE",
					ClientId: appClientId,
					Session: initiateResponse.Session,
					ChallengeResponses: {
						USERNAME: userId,
						// Pass the nonce as the ANSWER for VerifyAuthChallenge to validate
						ANSWER: nonce,
					},
				}),
			);
			return expectedAuthenticationResultToUser(AuthenticationResult);
		},
		refreshAuthUser,
		// Read the current attributes straight from Cognito (GetUser) instead of
		// the id token, so freshly-saved values are reflected and we don't depend
		// on the id token carrying them.
		async getAttributes(accessToken: AccessToken) {
			const response = await client.send(
				new GetUserCommand({ AccessToken: accessToken.accessToken }),
			);
			// We're getting full user, so should have all attributes, so casting to that
			return cognitoAttributesToProfile(
				response.UserAttributes ?? [],
			) as AuthUserAttributes;
		},
		async updateAttributes(
			accessToken: AccessToken,
			// TODO: Remove when stop using id token
			refreshToken: RefreshToken,
			newProfile: Partial<AuthUserAttributes>,
		) {
			await client.send(
				new UpdateUserAttributesCommand({
					AccessToken: accessToken.accessToken,
					UserAttributes: profileToCognitoAttributes(newProfile),
				}),
			);
			return refreshAuthUser(refreshToken);
		},
		async resendEmailVerifyCode({
			accessToken,
		}: Pick<AuthUser, "accessToken">) {
			await client.send(
				new GetUserAttributeVerificationCodeCommand({
					AccessToken: accessToken.accessToken,
					AttributeName: "email",
				}),
			);
		},
		async verifyEmail(
			{
				accessToken,
				refreshToken,
			}: Pick<AuthUser, "accessToken" | "refreshToken">,
			code: string,
		) {
			await client.send(
				new VerifyUserAttributeCommand({
					AccessToken: accessToken.accessToken,
					AttributeName: "email",
					Code: code,
				}),
			);
			return refreshAuthUser(refreshToken);
		},
		async signIn(emailAddress: string): Promise<SignInResult> {
			try {
				const { Session, ChallengeName } = await client.send(
					new InitiateAuthCommand({
						AuthFlow: "USER_AUTH",
						AuthParameters: {
							USERNAME: emailAddress,
							PREFERRED_CHALLENGE: "EMAIL_OTP",
						},
						ClientId: appClientId,
						// Note: ClientMetadata doesn't go through to user pool custom message handler
					}),
				);
				if (ChallengeName !== "EMAIL_OTP") {
					throw new Error(`Unexpected auth challenge ${ChallengeName}`);
				}
				if (Session === undefined) {
					throw new Error("Invalid sign in - no session");
				}
				return { type: "success", session: Session };
			} catch (e) {
				if (e instanceof UserNotFoundException) {
					return { type: "not-found" };
				}
				throw e;
			}
		},
		async signInConfirm({
			emailAddress,
			session,
			code,
		}: SignInConfirmRequest): Promise<AuthUser> {
			const { AuthenticationResult } = await client.send(
				new RespondToAuthChallengeCommand({
					ChallengeName: "EMAIL_OTP",
					ChallengeResponses: {
						USERNAME: emailAddress,
						EMAIL_OTP_CODE: code,
					},
					ClientId: appClientId,
					Session: session,
				}),
			);
			return expectedAuthenticationResultToUser(AuthenticationResult);
		},
	};
}

type AuthService = ReturnType<typeof createAuthService>;

export type {
	FullSignUpUser,
	SignInResult,
	SignUpResult,
	AuthService,
	SignInConfirmRequest,
};
export { createAuthService };
